module Steps.FormPicker
    ( formPickerWidget, renderFormPicker, formPickerStepWidget
    , FormPickerState, FormPickerAction(..), initFormPicker, loadFormPicker
    , emptyFormPickerState
    , setFormReorder, endFormReorder
    ) where

import Common

import API.Account (AccountType(..))
import API.FormBase (FormBasic)
import API.FormType (FormPickerMap, areFormsSelected, deselectForm, formListQuery, isFormSelected, selectForm, selectForms, setFormOrder)
import API.Search (SearchSemester, SearchTeacher)
import App.Route as AppRoute
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Monad.Except (ExceptT(..), runExceptT)
import Data.Array (catMaybes, mapWithIndex, updateAt, (!!), (:))
import GraphQL.Join (runGraphQL)
import GraphQL.Query (Query)
import GraphQL.Type (innerId)
import Internal.SelectionMap as Sel
import Internal.Util (justIf)
import App.Step (Navigation(..))
import Steps.WorkQueue.Cache (WorkQueueCache, initialWorkQueueCache)
import Steps.WorkQueue.State (WorkQueueState, formForSelected, initialWorkQueueState, isWorkQueueEmpty)
import Steps.WorkQueue.UIState (WorkQueueUiState, initialWorkQueueUiState)
import Steps.WorkQueue.View (WorkQueueResult, workQueueWidgetWithRoute)
import Widgets.BetterSelect (Select, selectedItem)
import Widgets.Checkbox (checkbox, labelCheckbox')
import Widgets.HiddenSelect (hiddenSelect)

type FormPickerState =
    { forms :: Array FormBasic
    , workQueueState :: WorkQueueState
    , workQueueUi :: WorkQueueUiState
    , workQueueCache :: WorkQueueCache
    , reorder :: Boolean
    , movedDownIdx :: Maybe Int
    }

formPickerStepWidget :: AppRoute.PreservedQuery -> FormPickerState -> Widget HTML FormPickerAction
formPickerStepWidget preservedQuery state = D.div
    [ P.className "step-container step-2b" ] $ catMaybes
    [ Just $ D.header
        [ P.className "page-header" ]
        [ D.h1
            [ P.className "page-heading" ]
            [ D.text $ lookupTranslation "select-forms" ]
        , actionBtns
        ]
    , Just $ WorkQR <$> workQueueWidgetWithRoute preservedQuery state.workQueueState state.workQueueUi state.workQueueCache
    , justIf (not $ formForSelected state.workQueueCache.account state.workQueueState) $
        D.div [ P.className "error" ] [ D.text $ lookupTranslation "must-select-teacher-semester" ]
    , Just $ D.div
        [ P.className "teacher-semester-selections" ] $ catMaybes
        [ Just $ D.p' [ D.text $ lookupTranslation "semester-hidden-select" ]
        , Just (State <$> setSemesterSelect state <$> hiddenSelect state.workQueueUi.semester)
        , justIf notBoardAcc $
            D.p' [ D.text $ lookupTranslation "teacher-hidden-select" ]
        , justIf notBoardAcc
            (State <$> setTeacherSelect state <$> hiddenSelect state.workQueueUi.teacher)
        ]
    , Just $ D.div
        [ P.className "step-2b-right" ]
        [ State <$> formPickerWidget state
        ]
    ]
  where
    notBoardAcc = state.workQueueCache.account.type /= BoardAccount

    actionBtns = D.div
        [ P.className "btn-group" ] $ catMaybes
        [ Just $ D.button
            [ P.className "btn cancel"
            , Navigate NavCancel <$ P.onClick
            ]
            [ D.text $ lookupTranslation "cancel" ]
        , justIf (not $ isWorkQueueEmpty state.workQueueState) $ D.button
            [ P.className "btn export"
            , P.disabled (Sel.isEmpty state.workQueueState.forms || not (formForSelected state.workQueueCache.account state.workQueueState))
            , Export <$ P.onClick
            ]
            [ D.text $ lookupTranslation "export-pdf" ]
        , Just $ D.button
            [ P.className "btn btn-primary"
            , P.disabled (Sel.isEmpty state.workQueueState.forms || not (formForSelected state.workQueueCache.account state.workQueueState))
            , Navigate NavNext <$ P.onClick
            ]
            [ D.text $ lookupTranslation "next" ]
        ]

data FormPickerAction
    = State FormPickerState
    | Navigate Navigation
    | WorkQR WorkQueueResult
    | Export

formPickerWidget :: FormPickerState -> Widget HTML FormPickerState
formPickerWidget fps = D.div
    [ P.className "form-picker-step" ]
    [ renderFormPicker fps
    ]

emptyFormPickerState :: FormPickerState
emptyFormPickerState =
    { forms: []
    , reorder: false
    , movedDownIdx: Nothing
    , workQueueState: initialWorkQueueState
    , workQueueUi: initialWorkQueueUiState
    , workQueueCache: initialWorkQueueCache
    }

initFormPicker
    :: WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Array FormBasic
    -> FormPickerState
initFormPicker workQueueState workQueueUi workQueueCache =
    { workQueueState, workQueueUi, workQueueCache, reorder: false, movedDownIdx: Nothing, forms: _ }

loadFormPicker :: WorkQueueState -> WorkQueueUiState -> WorkQueueCache -> Query FormPickerState
loadFormPicker workQueueState workQueueUi workQueueCache =
    initFormPicker workQueueState workQueueUi workQueueCache <$> formListQuery

-- | Set whether the given form picker is being re-ordered.
setFormReorder :: FormPickerState -> Boolean -> FormPickerState
setFormReorder fps reorder = fps { reorder = reorder }

-- | Save the order of the forms to the backend and return the form picker to normal mode.
endFormReorder :: FormPickerState -> Aff (Either ErrorWrapper FormPickerState)
endFormReorder fps = runExceptT do
    ExceptT $ runGraphQL $ setFormOrder $ map _.id fps.forms
    pure $ fps { reorder = false }

renderFormPicker :: FormPickerState -> Widget HTML FormPickerState
renderFormPicker state =
    D.div [ P.className "form-picker" ]
      $ selectAllFormsCheckbox state
      : render
  where
    render | state.reorder = renderFormArrows state
    render = renderFormCheckboxes state

selectAllFormsCheckbox :: FormPickerState -> Widget HTML FormPickerState
selectAllFormsCheckbox state = do
    v <- labelCheckbox' "form-picker-select-all" "form-picker-select-all" (lookupTranslation "select-all")
        (areFormsSelected state.forms state.workQueueState.forms)
    pure $ withForms state $ selectForms state.forms v

renderFormCheckboxes :: FormPickerState -> Array (Widget HTML FormPickerState)
renderFormCheckboxes state =
    map formCheckBoxRender state.forms
    where
        update form true = withForms state $ selectForm form
        update form false = withForms state $ deselectForm form

        selectedClass form =
            if isFormSelected form state.workQueueState.forms
            then " selected"
            else ""

        formCheckBoxRender form = D.div
            [ P.className $ "form-type" <> selectedClass form
            , P.key $ "form-type-" <> innerId form.id <> "-container"
            , P._id $ "form-type-" <> innerId form.id <> "-container"
            ]
            [ map (update form)
                $ checkbox [ P._id $ "form-" <> innerId form.id, P.className "form-checkbox" ]
                $ isFormSelected form state.workQueueState.forms
            , D.label
                [ P.htmlFor $ "form-" <> innerId form.id, P.className "form-label" ]
                [ D.abbr
                    [ P.className "form-abbr"
                    , P.style { backgroundColor: form.bgColor }
                    ]
                    [ D.text form.abbr ]
                , D.text form.label
                ]
            ]

renderFormArrows :: FormPickerState -> Array (Widget HTML FormPickerState)
renderFormArrows state =
    mapWithIndex formArrowRender state.forms
  where
    moveDown idx = case update of
        Nothing -> state
        Just new -> state { forms = new, movedDownIdx = Just (idx + 1) }
      where
        update = do
            this <- state.forms !! idx
            after <- state.forms !! (idx + 1)
            updateAt idx after state.forms >>= updateAt (idx + 1) this

    -- Was a form type at a given index moved up the ordering by the user?
    wasMovedDown idx | Just idx == state.movedDownIdx = true
    wasMovedDown _ = false

    -- Was a form type at a given index moved down the ordering by the user?
    wasMovedUp idx | Just (idx + 1) == state.movedDownIdx = true
    wasMovedUp _ = false

    formArrowRender idx form = D.div
        [ P.className
            $ "form-type"
            -- Classes denoting form types that moved up/down the order
            -- allow us to hook CSS animations into these changes.
            <> (if wasMovedDown idx then " moved-down" else "")
            <> (if wasMovedUp idx then " moved-up-" <> show (idx `mod` 2) else "")
        , P.key $ "form-type-" <> innerId form.id <> "-container"
        , P._id $ "form-type-" <> innerId form.id <> "-container"
        ]
        [ D.div [ P.className "form-arrows" ] $
            (if idx > 0 then
                [ D.button
                    [ P.className "arrow-up", moveDown (idx - 1) <$ P.onClick ]
                    [ D.span [ P.className $ "arrow-up-icon" ] []
                    , D.span [ P.className "sr-only" ] [ D.text $ lookupTranslation "up" ]
                    ]
                ]
            else
                [ D.span [ P.className $ "arrow-up-icon-first" ] []
                ])
                <>
            (if (idx < length state.forms - 1) then
                [ D.button
                    [ P.className "arrow-down", moveDown idx <$ P.onClick ]
                    [ D.span [ P.className $ "arrow-down-icon" ] []
                    , D.span [ P.className "sr-only" ] [ D.text $ lookupTranslation "down" ]
                    ]
                ]
            else
                [ D.span [ P.className $ "arrow-down-icon-last" ] []
                ])
        , D.label
            [ P.className "form-label" ]
            [ D.abbr
                [ P.className "form-abbr"
                , P.style { backgroundColor: form.bgColor }
                ]
                [ D.text form.abbr ]
            , D.text form.label
            ]
        ]

withForms :: FormPickerState -> (FormPickerMap -> FormPickerMap) -> FormPickerState
withForms state func = state { workQueueState { forms = func state.workQueueState.forms } }

setTeacherSelect :: FormPickerState -> Select SearchTeacher -> FormPickerState
setTeacherSelect state teacher =
    state
        { workQueueUi { teacher = teacher }
        , workQueueState { teacher = _.value <$> selectedItem teacher }
        }

setSemesterSelect :: FormPickerState -> Select SearchSemester -> FormPickerState
setSemesterSelect state semester =
    state
        { workQueueUi { semester = semester }
        , workQueueState { semester = _.value <$> selectedItem semester }
        }
